import { ProductBehavior as ProductBehaviorEnum } from '@up2stay/up2promo-api-client';
import {
  applyRoutingMode,
  formatSocialUsernameDisplay,
  getProductFormRoutingState,
  inferRoutingMode,
  parseInstagramUsername,
  parseTiktokUsername,
  hasFeedbackFunnel,
  type ProductFormValues,
  type ProductRoutingMode,
} from '@up2stay/up2promo-commons/products';
import { Label } from '@up2stay/up2promo-ui';
import { useMemo, useState } from 'react';

import { ProductFeedbackFunnelFields } from './product-feedback-funnel-fields';
import { ProductFormBehaviourField } from './product-form-fields/behaviour-field';
import { ProductFormDescriptionField } from './product-form-fields/description-field';
import { ProductFormRoutingSection } from './product-form-fields/routing-section';
import type { ProductFormFieldsProps } from './product-form-fields/types';
import {
  SettingsGroup,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';

export type { ProductFormFieldsProps };

export function ProductFormFields({
  values,
  onChange,
  knownProductType,
  workspaceId,
  locations,
  dict,
  idPrefix = 'product',
  showDescriptionHint = false,
  descriptionRequiredMessage,
  locationRequiredMessage,
  descriptionFirst = false,
  routingMode: routingModeProp,
  onRoutingModeChange,
  fieldErrors,
  onTargetUrlBlur,
  onTargetUrlFocus,
  onDescriptionBlur,
  onDescriptionFocus,
  hideProfileUserPicker = false,
  lockedProfileUserLabel,
  onCreateLocation,
  createLocationLabel,
  noLocationsTitle,
  noLocationsDescription,
  leading,
}: ProductFormFieldsProps) {
  const inferredMode = useMemo(
    () => inferRoutingMode(values, knownProductType),
    [values, knownProductType],
  );
  const [internalRoutingMode, setInternalRoutingMode] =
    useState<ProductRoutingMode | null>(null);

  const selectedRoutingMode =
    routingModeProp ?? internalRoutingMode ?? inferredMode;

  const routing = getProductFormRoutingState({
    values,
    knownProductType,
    locations,
    kindLabels: dict?.edit,
    routingModeOverride: selectedRoutingMode,
  });

  const [instagramUsername, setInstagramUsername] = useState(() =>
    formatSocialUsernameDisplay(parseInstagramUsername(values.targetUrl)),
  );
  const [tiktokUsername, setTiktokUsername] = useState(() =>
    formatSocialUsernameDisplay(parseTiktokUsername(values.targetUrl)),
  );
  const targetUrlErrorMessage = fieldErrors?.targetUrl;

  // Keep username fields aligned when targetUrl already encodes a handle/URL
  // (e.g. product detail load with `@ilias` or instagram.com/…).
  const igFromTarget = formatSocialUsernameDisplay(
    parseInstagramUsername(values.targetUrl),
  );
  const ttFromTarget = formatSocialUsernameDisplay(
    parseTiktokUsername(values.targetUrl),
  );
  if (
    selectedRoutingMode === 'instagramUsername' &&
    igFromTarget &&
    parseInstagramUsername(instagramUsername) !==
      parseInstagramUsername(igFromTarget) &&
    !instagramUsername.trim()
  ) {
    setInstagramUsername(igFromTarget);
  }
  if (
    selectedRoutingMode === 'tiktokUsername' &&
    ttFromTarget &&
    parseTiktokUsername(tiktokUsername) !== parseTiktokUsername(ttFromTarget) &&
    !tiktokUsername.trim()
  ) {
    setTiktokUsername(ttFromTarget);
  }

  const handleRoutingModeChange = (mode: ProductRoutingMode) => {
    if (routingModeProp === undefined) {
      setInternalRoutingMode(mode);
    }
    onRoutingModeChange?.(mode);
    if (mode === 'instagramUsername') {
      setInstagramUsername(
        formatSocialUsernameDisplay(parseInstagramUsername(values.targetUrl)),
      );
    }
    if (mode === 'tiktokUsername') {
      setTiktokUsername(
        formatSocialUsernameDisplay(parseTiktokUsername(values.targetUrl)),
      );
    }
    onChange(applyRoutingMode(mode, values, knownProductType));
  };

  const behaviourField = routing.showBehaviourSelector ? (
    <ProductFormBehaviourField
      value={values.targetKind}
      onChange={(targetKind) => {
        const patch: Partial<ProductFormValues> = { targetKind };
        if (targetKind !== ProductBehaviorEnum.URL) {
          patch.targetUrl = '';
        }
        if (targetKind !== ProductBehaviorEnum.PROFILE) {
          patch.userId = null;
        }
        if (
          targetKind !== ProductBehaviorEnum.GOOGLE_REVIEW &&
          targetKind !== ProductBehaviorEnum.INSTAGRAM &&
          targetKind !== ProductBehaviorEnum.MENU &&
          targetKind !== ProductBehaviorEnum.FACEBOOK &&
          targetKind !== ProductBehaviorEnum.TIKTOK
        ) {
          patch.locationId = null;
        }
        onChange(patch);
      }}
      dict={dict}
    />
  ) : (
    <div className="space-y-0.5 min-w-0 w-full">
      <Label className="text-muted-foreground">{dict?.edit?.productKind}</Label>
      <p className="text-sm font-medium">{routing.productKindLabel}</p>
    </div>
  );

  const routingSection = (
    <ProductFormRoutingSection
      values={values}
      onChange={onChange}
      knownProductType={knownProductType}
      workspaceId={workspaceId}
      locations={locations}
      dict={dict}
      idPrefix={idPrefix}
      locationRequiredMessage={locationRequiredMessage}
      hideProfileUserPicker={hideProfileUserPicker}
      lockedProfileUserLabel={lockedProfileUserLabel}
      onCreateLocation={onCreateLocation}
      createLocationLabel={createLocationLabel}
      noLocationsTitle={noLocationsTitle}
      noLocationsDescription={noLocationsDescription}
      routing={routing}
      onRoutingModeChange={handleRoutingModeChange}
      instagramUsername={instagramUsername}
      onInstagramUsernameChange={setInstagramUsername}
      tiktokUsername={tiktokUsername}
      onTiktokUsernameChange={setTiktokUsername}
      onTargetUrlBlur={onTargetUrlBlur}
      onTargetUrlFocus={onTargetUrlFocus}
      targetUrlErrorMessage={targetUrlErrorMessage}
    />
  );

  const descriptionField = (
    <ProductFormDescriptionField
      id={`${idPrefix}-description`}
      value={values.description}
      onChange={(description) => onChange({ description })}
      onBlur={onDescriptionBlur}
      onFocus={onDescriptionFocus}
      label={dict?.edit?.description}
      // Required here, so no "(optional)" from the wizard copy (DEV-568).
      placeholder={
        dict?.edit?.descriptionPlaceholder ?? dict?.edit?.description
      }
      hint={showDescriptionHint ? dict?.setup?.descriptionHint : undefined}
      requiredMessage={
        fieldErrors !== undefined
          ? fieldErrors.description
          : !values.description.trim()
            ? descriptionRequiredMessage
            : undefined
      }
    />
  );

  const feedbackFunnelSection = hasFeedbackFunnel(values) ? (
    <ProductFeedbackFunnelFields
      values={{
        feedbackMode: values.feedbackMode,
        reviewThreshold: values.reviewThreshold,
        feedbackQuestions: values.feedbackQuestions,
      }}
      onChange={onChange}
      dict={dict}
      idPrefix={`${idPrefix}-feedback`}
    />
  ) : null;

  const routingTitle =
    dict?.edit?.routing?.title ?? dict?.edit?.routingTitle ?? 'Destination';
  const routingInfo =
    dict?.edit?.routing?.subtitle ??
    (selectedRoutingMode === 'profile'
      ? dict?.edit?.routing?.profileHint
      : undefined);

  const hasLeading = leading != null && leading !== false;
  const hasBehaviourRow =
    routing.showBehaviourSelector || Boolean(routing.productKindLabel);

  const basicsRows = descriptionFirst ? (
    <SettingsGroupRows>
      {hasLeading ? <SettingsGroupRow>{leading}</SettingsGroupRow> : null}
      {hasBehaviourRow ? (
        <SettingsGroupRow separated={hasLeading}>
          {behaviourField}
        </SettingsGroupRow>
      ) : null}
      <SettingsGroupRow separated={hasLeading || hasBehaviourRow}>
        {descriptionField}
      </SettingsGroupRow>
    </SettingsGroupRows>
  ) : (
    <SettingsGroupRows>
      {hasLeading ? <SettingsGroupRow>{leading}</SettingsGroupRow> : null}
      <SettingsGroupRow separated={hasLeading}>
        {descriptionField}
      </SettingsGroupRow>
      {hasBehaviourRow ? (
        <SettingsGroupRow separated>{behaviourField}</SettingsGroupRow>
      ) : null}
    </SettingsGroupRows>
  );

  return (
    <div className="flex min-w-0 w-full flex-col gap-6">
      {/* No section label — PageHeader already names the product. */}
      <SettingsGroup>{basicsRows}</SettingsGroup>

      <SettingsSection
        id={`${idPrefix}-routing`}
        title={routingTitle}
        info={routingInfo}
        grouped
      >
        {routingSection}
      </SettingsSection>

      {feedbackFunnelSection}
    </div>
  );
}
